<template>
  <div class="configuracao-cobranca-form">
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h5 class="mb-1">
          {{
            isEdit
              ? 'Editar Configuração Bancária'
              : 'Nova Configuração Bancária'
          }}
        </h5>

        <small class="text-muted">
          Os campos específicos da integração são definidos pelo catálogo do backend.
        </small>
      </div>

      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="voltar"
      >
        Voltar
      </button>
    </div>

    <div
      v-if="
        permissoesCarregadas &&
        !podeConfigurar
      "
      class="alert alert-warning"
    >
      Você não possui permissão para administrar configurações bancárias.
    </div>

    <div
      v-else-if="loading"
      class="text-muted py-3"
    >
      Carregando configuração bancária...
    </div>

    <template v-else-if="podeConfigurar">
      <div
        v-if="errorMessage"
        class="alert alert-danger"
      >
        {{ errorMessage }}
      </div>

      <div
        v-if="
          isEdit &&
          detalhe &&
          detalhe.ativo
        "
        class="alert alert-warning"
      >
        A configuração está ativa e não pode ser editada.
        Desative-a antes de alterar seus parâmetros.
      </div>

      <form
        class="card shadow-sm"
        @submit.prevent="salvar"
      >
        <div class="card-body">
          <h6 class="mb-3">
            Identidade da integração
          </h6>

          <div class="row g-3">
            <div class="col-md-6">
              <label
                class="form-label"
                for="conta-financeira"
              >
                Conta financeira
                <span class="text-danger">*</span>
              </label>

              <select
                id="conta-financeira"
                v-model="form.conta_financeira_id"
                class="form-select"
                :disabled="isEdit || !editable"
                required
              >
                <option value="">
                  Selecione
                </option>

                <option
                  v-for="conta in contasDisponiveis"
                  :key="conta.id"
                  :value="conta.id"
                >
                  {{ contaLabel(conta) }}
                </option>
              </select>
            </div>

            <div
              v-if="!isEdit"
              class="col-md-6"
            >
              <label
                class="form-label"
                for="integracao"
              >
                Integração
                <span class="text-danger">*</span>
              </label>

              <select
                id="integracao"
                v-model="catalogKey"
                class="form-select"
                :disabled="!editable"
                required
                @change="onCatalogChange"
              >
                <option value="">
                  Selecione
                </option>

                <option
                  v-for="item in catalogo"
                  :key="catalogItemKey(item)"
                  :value="catalogItemKey(item)"
                >
                  {{ item.provedor }} — {{ item.adaptador }}
                </option>
              </select>
            </div>

            <template v-else>
              <div class="col-md-3">
                <label class="form-label">
                  Provedor
                </label>

                <input
                  class="form-control"
                  :value="form.provedor"
                  disabled
                >
              </div>

              <div class="col-md-3">
                <label class="form-label">
                  Adaptador
                </label>

                <input
                  class="form-control"
                  :value="form.adaptador"
                  disabled
                >
              </div>
            </template>

            <div class="col-md-4">
              <label
                class="form-label"
                for="ambiente"
              >
                Ambiente
                <span class="text-danger">*</span>
              </label>

              <select
                id="ambiente"
                v-model="form.ambiente"
                class="form-select"
                :disabled="isEdit || !editable"
                required
              >
                <option value="">
                  Selecione
                </option>

                <option
                  v-for="environment in selectedDefinition?.ambientes || []"
                  :key="environment"
                  :value="environment"
                >
                  {{ environment }}
                </option>
              </select>
            </div>

            <div class="col-md-4">
              <label class="form-label d-block">
                Capacidade
              </label>

              <div class="form-check mt-2">
                <input
                  id="boleto-pix"
                  v-model="form.permite_boleto_pix"
                  class="form-check-input"
                  type="checkbox"
                  :disabled="!editable"
                >

                <label
                  class="form-check-label"
                  for="boleto-pix"
                >
                  Permitir Boleto + Pix
                </label>
              </div>
            </div>
          </div>

          <hr class="my-4">

          <h6 class="mb-3">
            Parâmetros da integração
          </h6>

          <div
            v-if="
              selectedDefinition &&
              !selectedDefinition.parametros.length
            "
            class="text-muted mb-3"
          >
            Esta integração não exige parâmetros adicionais.
          </div>

          <div class="row g-3">
            <div
              v-for="parameter in selectedDefinition?.parametros || []"
              :key="parameter.campo"
              class="col-md-6"
            >
              <label
                class="form-label"
                :for="`param-${parameter.campo}`"
              >
                {{ humanize(parameter.campo) }}

                <span
                  v-if="parameter.obrigatorio"
                  class="text-danger"
                >
                  *
                </span>
              </label>

              <input
                :id="`param-${parameter.campo}`"
                v-model="parameterValues[parameter.campo]"
                class="form-control"
                type="text"
                :disabled="!editable || identidadeUtilizada"
                :required="parameter.obrigatorio"
                autocomplete="off"
              >
            </div>
          </div>

          <hr class="my-4">

          <section v-if="isSicredi" aria-labelledby="credentials-title" class="mb-4">
            <h6 id="credentials-title" class="mb-2">Credenciais de acesso</h6>
            <p v-if="detalhe?.credencial_configurada" class="text-success">
              {{ externalCredentials ? '✓ Credenciais configuradas externamente' : '✓ Credenciais configuradas' }}
            </p>
            <button v-if="externalCredentials && !updatingCredentials" type="button"
              class="btn btn-outline-primary btn-sm" :disabled="!editable"
              @click="updatingCredentials = true">Atualizar credenciais</button>
            <div v-if="!externalCredentials || updatingCredentials" class="row g-3">
              <div class="col-md-6">
                <label class="form-label" for="codigo-acesso">Código de Acesso</label>
                <input id="codigo-acesso" v-model="codigoAcesso" class="form-control"
                  type="password" autocomplete="new-password" :disabled="!editable"
                  :placeholder="credentialPlaceholder" maxlength="4096">
              </div>
              <div class="col-md-6">
                <label class="form-label" for="token-acesso">Token de Acesso (x-api-key)</label>
                <input id="token-acesso" v-model="tokenAcesso" class="form-control"
                  type="password" autocomplete="new-password" :disabled="!editable"
                  :placeholder="credentialPlaceholder" aria-describedby="token-help" maxlength="4096">
                <small id="token-help" class="text-muted">Credencial fornecida pelo SICREDI. O token OAuth é obtido automaticamente.</small>
              </div>
              <p v-if="detalhe?.credencial_configurada" class="text-muted small mb-0">
                Preencha os dois campos somente se desejar substituir as credenciais.
              </p>
            </div>
          </section>

          <template v-if="visibleReferences.length || !isSicredi">
          <h6 class="mb-2">
            Referências seguras
          </h6>

          <p class="text-muted small">
            Informe apenas referências no formato ENV:NOME_DA_VARIAVEL.
            O valor real da credencial nunca é armazenado neste formulário.
          </p>

          <div
            v-if="
              selectedDefinition &&
              !selectedDefinition.referencias_segredo.length
            "
            class="text-muted mb-3"
          >
            Esta integração não exige referências de segredo.
          </div>

          <div class="row g-3">
            <div
              v-for="reference in visibleReferences"
              :key="reference.campo"
              class="col-md-6"
            >
              <label
                class="form-label"
                :for="`secret-${reference.campo}`"
              >
                {{ humanize(reference.campo) }}

                <span
                  v-if="
                    reference.obrigatorio &&
                    !referenceConfigured(reference)
                  "
                  class="text-danger"
                >
                  *
                </span>
              </label>

              <input
                :id="`secret-${reference.campo}`"
                v-model="secretValues[reference.campo]"
                class="form-control"
                type="text"
                :disabled="
                  !editable ||
                  Boolean(clearReferences[reference.campo])
                "
                autocomplete="off"
                spellcheck="false"
                placeholder="ENV:NOME_DA_VARIAVEL"
              >

              <small
                v-if="
                  isEdit &&
                  referenceConfigured(reference)
                "
                class="text-muted d-block mt-1"
              >
                Já configurada. Deixe em branco para manter a referência atual.
              </small>

              <div
                v-if="
                  isEdit &&
                  referenceConfigured(reference) &&
                  !reference.obrigatorio
                "
                class="form-check mt-2"
              >
                <input
                  :id="`clear-${reference.campo}`"
                  v-model="clearReferences[reference.campo]"
                  class="form-check-input"
                  type="checkbox"
                  :disabled="!editable"
                >

                <label
                  class="form-check-label"
                  :for="`clear-${reference.campo}`"
                >
                  Remover referência configurada
                </label>
              </div>
            </div>
          </div>
          </template>
        </div>

        <div class="card-body border-top">
          <h6>Beneficiário e criação automática</h6>
          <p class="text-muted small">O beneficiário é o proprietário cadastrado da conta. Confira seu documento, endereço e vínculo com o convênio antes de emitir. A marca da escola permanece separada.</p>
          <div v-if="proprietarioConta" class="alert alert-light border small">
            <strong>{{ proprietarioConta.nome || 'Proprietário sem nome cadastrado' }}</strong> · {{ proprietarioConta.documento }}
            <div>{{ [proprietarioConta.logradouro, proprietarioConta.numero, proprietarioConta.bairro, proprietarioConta.municipio, proprietarioConta.uf, proprietarioConta.cep].filter(Boolean).join(' · ') }}</div>
            <div>Dados atuais do proprietário da conta. A conferência do vínculo bancário é uma decisão explícita.</div>
          </div>
          <p v-if="erroProprietario" class="text-danger small">{{ erroProprietario }}</p>
          <div class="row g-3">
            <div class="col-md-6">
              <label for="beneficiario-documento" class="form-label">CPF/CNPJ do beneficiário conferido</label>
              <input id="beneficiario-documento" v-model="beneficiarioDocumento" class="form-control" :disabled="!editable || conferenciaPreservada" maxlength="18" autocomplete="off">
            </div>
            <div class="col-md-6">
              <label for="beneficiario-referencia" class="form-label">Referência da conferência do convênio</label>
              <input id="beneficiario-referencia" v-model="beneficiarioReferencia" class="form-control" :disabled="!editable || conferenciaPreservada" maxlength="200" placeholder="Ex.: documento bancário conferido em 02/10/2026">
              <small class="text-muted">Use somente identificação do documento. Não informe senhas ou tokens.</small>
            </div>
          </div>
          <div class="form-check mt-3">
            <input id="beneficiario-conferido" v-model="beneficiarioConferido" type="checkbox" class="form-check-input" :disabled="!editable || conferenciaPreservada">
            <label for="beneficiario-conferido" class="form-check-label">Conferi que o proprietário da conta corresponde ao beneficiário deste convênio</label>
          </div>
          <p v-if="identidadeUtilizada" class="text-muted small mt-2">Códigos bancários preservados por cobranças existentes. A primeira conferência do beneficiário ainda pode ser completada; uma conferência já utilizada não pode ser reescrita. Novo convênio exige nova configuração; credenciais do mesmo convênio podem ser atualizadas.</p>
          <div class="form-check mt-3">
            <input id="selecao-automatica" v-model="selecaoAutomatica" type="checkbox" class="form-check-input" :disabled="!editable">
            <label for="selecao-automatica" class="form-check-label">Usar esta configuração na criação automática de boletos desta conta</label>
          </div>
          <small class="text-muted">Escolha exatamente uma configuração ativa por conta para boleto. Ausência ou mais de uma escolha bloqueia a criação com orientação para revisão. Salvar não ativa a configuração.</small>
        </div>

        <div
          class="card-footer d-flex justify-content-end gap-2"
        >
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="saving"
            @click="voltar"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-primary"
            :disabled="
              saving ||
              !editable
            "
          >
            {{
              saving
                ? 'Salvando...'
                : 'Salvar'
            }}
          </button>
        </div>
      </form>
    </template>
  </div>
</template>


<script setup>
import { contasParaConfiguracao } from "@/utils/boletosCapability.mjs";
import {
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  reactive,
  ref
} from 'vue';

import {
  useRoute,
  useRouter
} from 'vue-router';

import {
  usePermissions
} from '@/composables/usePermissions';

import configuracoesCobrancasApi
  from '@/services/configuracoesCobrancas.api';


const route =
  useRoute();

const router =
  useRouter();

const {
  fetchPermissions,
  hasPermission
} = usePermissions();


const SECRET_STATUS_FIELDS =
  Object.freeze({
    credencial_referencia:
      'credencial_configurada',

    certificado_referencia:
      'certificado_configurado',

    webhook_secret_referencia:
      'webhook_secret_configurado'
  });


const ENV_REFERENCE_RE =
  /^ENV:[A-Z][A-Z0-9_]{2,119}$/;


const catalogo =
  ref([]);

const contas =
  ref([]);

const detalhe =
  ref(null);

const catalogKey =
  ref('');

const loading =
  ref(true);

const saving =
  ref(false);

const errorMessage =
  ref('');

const permissoesCarregadas =
  ref(false);


const form =
  reactive({
    conta_financeira_id: '',
    provedor: '',
    adaptador: '',
    ambiente: '',
    permite_boleto_pix: true
  });


const parameterValues =
  reactive({});

const secretValues =
  reactive({});

const clearReferences =
  reactive({});


const isEdit =
  computed(
    () =>
      route.params.id !==
        undefined
  );


const podeConfigurar =
  computed(
    () =>
      hasPermission(
        'financeiro_cobrancas_configurar'
      )
  );


const editable =
  computed(
    () =>
      (
        podeConfigurar.value &&
        (
          !isEdit.value ||
          detalhe.value?.ativo === false
        )
      )
  );


const selectedDefinition =
  computed(
    () =>
      catalogo.value.find(
        item =>
          catalogItemKey(item) ===
            catalogKey.value
      ) ||
      null
  );


const proprietarioConta = ref(null);
const erroProprietario = ref('');
let proprietarioRequest = 0;
watch(() => form.conta_financeira_id, async id => {
  const request = ++proprietarioRequest;
  proprietarioConta.value = null; erroProprietario.value = '';
  if (!id) return;
  try {
    const data = await configuracoesCobrancasApi.obterBeneficiarioConta(id);
    if (request === proprietarioRequest) proprietarioConta.value = data;
  } catch { if (request === proprietarioRequest) erroProprietario.value = 'Não foi possível consultar o proprietário da conta. Revise o cadastro antes de conferir o beneficiário.'; }
});
const beneficiarioDocumento = ref('');
const beneficiarioReferencia = ref('');
const beneficiarioConferido = ref(false);
const selecaoAutomatica = ref(false);
const identidadeUtilizada = computed(() => Number(detalhe.value?.quantidade_cobrancas) > 0);
// First conference is permitted on a used legacy configuration; bank parameters stay frozen.
const conferenciaPreservada = computed(() => identidadeUtilizada.value && Boolean(detalhe.value?.beneficiario_conferencia));
const codigoAcesso = ref('');
const tokenAcesso = ref('');
const updatingCredentials = ref(false);
const isSicredi = computed(() => selectedDefinition.value?.adaptador === 'SICREDI_COBRANCA_API');
const externalCredentials = computed(() => detalhe.value?.credenciais_origem === 'EXTERNAL_ENV');
const credentialPlaceholder = computed(() => detalhe.value?.credencial_configurada
  ? 'Configurado — preencha somente para alterar' : '');
const visibleReferences = computed(() => (selectedDefinition.value?.referencias_segredo || [])
  .filter(reference => !isSicredi.value || reference.campo !== 'credencial_referencia'));
function clearCredentials() {
  codigoAcesso.value = '';
  tokenAcesso.value = '';
  updatingCredentials.value = false;
}
onBeforeUnmount(clearCredentials);


function catalogItemKey(item) {
  return (
    `${item.provedor}::${item.adaptador}`
  );
}


function humanize(value) {
  const normalized =
    String(value || '')
      .replaceAll('_', ' ')
      .trim();

  if (!normalized) {
    return '';
  }

  return (
    normalized
      .charAt(0)
      .toUpperCase() +
    normalized.slice(1)
  );
}


function contaLabel(conta) {
  const principal =
    conta.apelido ||
    conta.instituicao ||
    `Conta #${conta.id}`;

  const fragments = [
    principal,

    conta.agencia
      ? `Ag ${conta.agencia}`
      : null,

    conta.numero_conta
      ? `CC ${conta.numero_conta}`
      : null
  ].filter(Boolean);

  return fragments.join(' | ');
}


function clearReactiveObject(object) {
  for (
    const key
    of Object.keys(object)
  ) {
    delete object[key];
  }
}


function initializeDefinitionValues(
  definition,
  configuration = null
) {
  clearCredentials();
  clearReactiveObject(
    parameterValues
  );

  clearReactiveObject(
    secretValues
  );

  clearReactiveObject(
    clearReferences
  );

  if (!definition) {
    return;
  }

  for (
    const parameter
    of definition.parametros
  ) {
    parameterValues[
      parameter.campo
    ] =
      configuration?.[
        parameter.campo
      ] ??
      '';
  }

  for (
    const reference
    of definition.referencias_segredo
  ) {
    secretValues[
      reference.campo
    ] = '';

    clearReferences[
      reference.campo
    ] = false;
  }
}


function onCatalogChange() {
  const definition =
    selectedDefinition.value;

  if (!definition) {
    form.provedor = '';
    form.adaptador = '';
    form.ambiente = '';

    initializeDefinitionValues(
      null
    );

    return;
  }

  form.provedor =
    definition.provedor;

  form.adaptador =
    definition.adaptador;

  form.ambiente =
    definition.ambientes[0] ||
    '';

  initializeDefinitionValues(
    definition
  );
}


function referenceConfigured(
  reference
) {
  const flag =
    SECRET_STATUS_FIELDS[
      reference.campo
    ];

  return Boolean(
    flag &&
    detalhe.value?.[flag]
  );
}


function normalizeOptionalInput(
  value
) {
  const text =
    String(value ?? '')
      .trim();

  return text || null;
}


function apiErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    'Não foi possível salvar a configuração bancária.'
  );
}


function validateForm() {
  if (
    !Number.isSafeInteger(
      Number(
        form.conta_financeira_id
      )
    ) ||
    Number(
      form.conta_financeira_id
    ) <= 0
  ) {
    return 'Selecione a conta financeira.';
  }

  const definition =
    selectedDefinition.value;

  if (!definition) {
    return 'Selecione uma integração válida.';
  }

  if (
    !definition.ambientes.includes(
      form.ambiente
    )
  ) {
    return 'Selecione um ambiente válido.';
  }

  for (
    const parameter
    of definition.parametros
  ) {
    const value =
      String(
        parameterValues[
          parameter.campo
        ] ??
        ''
      ).trim();

    if (
      parameter.obrigatorio &&
      !value
    ) {
      return (
        `${humanize(parameter.campo)} é obrigatório.`
      );
    }
  }

  for (
    const reference
    of visibleReferences.value
  ) {
    const value =
      String(
        secretValues[
          reference.campo
        ] ??
        ''
      ).trim();

    const configured =
      referenceConfigured(
        reference
      );

    const clearing =
      Boolean(
        clearReferences[
          reference.campo
        ]
      );

    if (
      reference.obrigatorio &&
      !value &&
      !configured
    ) {
      return (
        `${humanize(reference.campo)} é obrigatória.`
      );
    }

    if (
      reference.obrigatorio &&
      clearing
    ) {
      return (
        `${humanize(reference.campo)} é obrigatória e não pode ser removida.`
      );
    }

    if (
      value &&
      !ENV_REFERENCE_RE.test(
        value
      )
    ) {
      return (
        `${humanize(reference.campo)} deve usar o formato ENV:NOME_DA_VARIAVEL.`
      );
    }
  }

  if (isSicredi.value && !beneficiarioConferido.value) return 'Confira o beneficiário e o vínculo com o convênio.';
  if (beneficiarioConferido.value && (!beneficiarioDocumento.value.trim() || !beneficiarioReferencia.value.trim())) return 'Informe documento e referência da conferência.';
  if (isSicredi.value) {
    if (Boolean(codigoAcesso.value) !== Boolean(tokenAcesso.value)) {
      return 'Informe Código de Acesso e Token de Acesso juntos.';
    }
    if (!detalhe.value?.credencial_configurada && !codigoAcesso.value) {
      return 'Informe Código de Acesso e Token de Acesso.';
    }
  }
  return null;
}


function buildPayload() {
  const definition =
    selectedDefinition.value;

  const payload = {
    selecao_automatica: selecaoAutomatica.value,
    permite_boleto_pix:
      Boolean(
        form.permite_boleto_pix
      )
  };

  if (!isEdit.value) {
    Object.assign(
      payload,
      {
        conta_financeira_id:
          Number(
            form.conta_financeira_id
          ),

        provedor:
          form.provedor,

        adaptador:
          form.adaptador,

        ambiente:
          form.ambiente
      }
    );
  }

  for (
    const parameter
    of definition.parametros
  ) {
    payload[
      parameter.campo
    ] =
      normalizeOptionalInput(
        parameterValues[
          parameter.campo
        ]
      );
  }

  for (
    const reference
    of visibleReferences.value
  ) {
    const value =
      String(
        secretValues[
          reference.campo
        ] ??
        ''
      ).trim();

    if (value) {
      payload[
        reference.campo
      ] = value;

      continue;
    }

    if (
      isEdit.value &&
      clearReferences[
        reference.campo
      ] === true &&
      reference.obrigatorio === false
    ) {
      payload[
        reference.campo
      ] = null;
    }

    /*
     * Em edição, ausência do campo preserva
     * a referência existente no backend.
     */
  }

  if (isSicredi.value && (codigoAcesso.value || tokenAcesso.value)) {
    payload.codigo_acesso = codigoAcesso.value;
    payload.token_acesso = tokenAcesso.value;
  }
  if (beneficiarioConferido.value && !conferenciaPreservada.value) payload.beneficiario_conferencia = { documento: beneficiarioDocumento.value, referencia: beneficiarioReferencia.value };
  return payload;
}


const contasDisponiveis = computed(() => contasParaConfiguracao(contas.value, isEdit.value, form.conta_financeira_id));

async function carregarBase() {
  const [
    catalogResult,
    accountResult
  ] =
    await Promise.all([
      configuracoesCobrancasApi
        .obterCatalogo(),

      configuracoesCobrancasApi
        .listarContasFinanceiras()
    ]);

  catalogo.value =
    catalogResult;

  contas.value =
    accountResult;
}


async function carregarEdicao() {
  const id =
    Number(
      route.params.id
    );

  if (
    !Number.isSafeInteger(id) ||
    id <= 0
  ) {
    throw new Error(
      'Identificador de configuração inválido.'
    );
  }

  const configuration =
    await configuracoesCobrancasApi
      .detalhar(id);

  detalhe.value = configuration;
  beneficiarioDocumento.value = configuration.beneficiario_conferencia?.documento || '';
  beneficiarioReferencia.value = configuration.beneficiario_conferencia?.referencia || '';
  beneficiarioConferido.value = Boolean(configuration.beneficiario_conferencia);
  selecaoAutomatica.value = configuration.selecao_automatica;

  form.conta_financeira_id =
    configuration
      .conta_financeira_id;

  form.provedor =
    configuration.provedor;

  form.adaptador =
    configuration.adaptador;

  form.ambiente =
    configuration.ambiente;

  form.permite_boleto_pix =
    configuration
      .permite_boleto_pix;

  catalogKey.value =
    `${configuration.provedor}::${configuration.adaptador}`;

  const definition =
    selectedDefinition.value;

  if (!definition) {
    throw new Error(
      'A integração desta configuração não está disponível no catálogo administrativo.'
    );
  }

  if (
    !definition.ambientes.includes(
      configuration.ambiente
    )
  ) {
    throw new Error(
      'O ambiente da configuração não pertence ao catálogo administrativo.'
    );
  }

  initializeDefinitionValues(
    definition,
    configuration
  );
}


async function salvar() {
  if (
    saving.value ||
    !editable.value
  ) {
    return;
  }

  errorMessage.value = '';

  const validationMessage =
    validateForm();

  if (validationMessage) {
    errorMessage.value =
      validationMessage;

    return;
  }

  saving.value = true;

  try {
    const payload =
      buildPayload();

    if (isEdit.value) {
      await configuracoesCobrancasApi
        .atualizar(
          Number(
            route.params.id
          ),
          payload
        );
    } else {
      await configuracoesCobrancasApi
        .criar(
          payload
        );
    }

    clearCredentials();
    delete payload.codigo_acesso;
    delete payload.token_acesso;
    await router.push(
      '/financeiro/cobrancas/configuracoes'
    );
  } catch (error) {
    errorMessage.value =
      apiErrorMessage(
        error
      );
  } finally {
    saving.value = false;
  }
}


function voltar() {
  router.push(
    '/financeiro/cobrancas/configuracoes'
  );
}


onMounted(
  async () => {
    try {
      await fetchPermissions();
    } finally {
      permissoesCarregadas.value =
        true;
    }

    if (!podeConfigurar.value) {
      loading.value = false;
      return;
    }

    try {
      await carregarBase();

      if (isEdit.value) {
        await carregarEdicao();
      }
    } catch (error) {
      errorMessage.value =
        apiErrorMessage(
          error
        );
    } finally {
      loading.value = false;
    }
  }
);
</script>


<style scoped>
.configuracao-cobranca-form {
  width: 100%;
}

.form-label {
  font-weight: 600;
}
</style>
